import { useLocation } from "react-router-dom";
import { StorefrontSettingsProvider } from "./StorefrontSettingsProvider";
import { ExclamationCircleIcon } from "@heroicons/react/24/outline";
import { AdminUI } from "../../ui/AdminUI";
import EmptyPage from "../EmptyPage/EmptyPage";

export interface TabConfig {
    [key: string]: {
        title: string;
        path: string;
        component: React.FC;
        config: Record<string, boolean>;
        showFooterActions?: boolean;
    };
}

/**
 * Routes a settings sub-path to its section, inside the shared form provider.
 *
 * The tab strip this used to render is gone — the rail lists every destination
 * instead. A path matching no section now falls back to the first one rather
 * than rendering "Page Not Found": the bare /settings/storefront URL matched no
 * tab and produced a dead page, which is exactly the bug this removes.
 */
export const TabbedSettings: React.FC<{ title: string; tabConfig: TabConfig }> = ({ title, tabConfig }) => {
    const location = useLocation();
    const keys = Object.keys(tabConfig);

    if (keys.length === 0) {
        return <AdminUI><EmptyPage
            title="Page not found"
            description={`The page you are looking for does not exist.`}
            icon={ExclamationCircleIcon}
        />
        </AdminUI>;
    }

    const currentTab = keys.find((key) => location.pathname.includes(tabConfig[key].path)) ?? keys[0];
    const { component: CurrentTabComponent, config, showFooterActions = true } = tabConfig[currentTab];

    return (
        <StorefrontSettingsProvider
            title={title}
            fieldConfig={config}
            showFooterActions={showFooterActions}
        >
            <CurrentTabComponent />
        </StorefrontSettingsProvider>
    );
};
